import { useState, useEffect } from "react";
import { axios, Button, Card, CardContent, CardHeader, CardTitle, Badge, Input, Label, Loader2, Users, Gift, Zap, Copy, Twitter, Linkedin, Mail, MessageCircle, Send, TrendingUp, Trophy, Clock, API, useAuth, toast } from "@/pages/shared";
import { CLAIMS } from "@/config/claims";

const SHARE_BLURB = encodeURIComponent(`I'm using Creatara to generate Twitter threads, LinkedIn posts and blog articles in one click. You get ${CLAIMS.referral.creditsPerSignup} free credits with my link:`);

export default function ReferralsPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite form state
  const [emailDraft, setEmailDraft] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/referrals/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/leaderboard?limit=5`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(s.data);
      setLeaderboard(l.data.leaders || []);
    } catch (e) {
      toast.error("Failed to load referral stats");
    }
    setLoading(false);
  };

  useEffect(() => { loadStats(); /* eslint-disable-line */ }, [token]);

  if (loading || !stats) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  }

  const referralLink = stats.referral_link;
  const copyLink = () => { navigator.clipboard.writeText(referralLink); toast.success("Link copied to clipboard"); };
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${SHARE_BLURB}%20${encodeURIComponent(referralLink)}`, "_blank");
  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, "_blank");
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${SHARE_BLURB}%20${encodeURIComponent(referralLink)}`, "_blank");
  const shareEmail = () => {
    const subject = encodeURIComponent("Thought you'd like this — Creatara");
    const body = encodeURIComponent(`Hey,\n\nI've been using Creatara to crank out Twitter threads, LinkedIn posts and blog articles in minutes — figured you'd get a lot out of it too. Sign up with my link and we both get ${CLAIMS.referral.creditsPerSignup} free credits:\n\n${referralLink}\n`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const addRecipient = () => {
    const e = emailDraft.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    if (recipients.includes(e)) { toast.error("Already added"); return; }
    if (recipients.length >= 5) { toast.error("Max 5 emails per send"); return; }
    setRecipients([...recipients, e]);
    setEmailDraft("");
  };

  const removeRecipient = (e) => setRecipients(recipients.filter(r => r !== e));

  const sendInvites = async () => {
    if (recipients.length === 0) { toast.error("Add at least one recipient"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/referrals/invite-emails`,
        { emails: recipients, note },
        { headers: { Authorization: `Bearer ${token}` } });
      const sent = res.data.sent.length;
      const skipped = res.data.skipped;
      if (sent > 0) toast.success(`${sent} invitation${sent > 1 ? "s" : ""} sent`);
      skipped.forEach(s => {
        const reason = s.reason === "already_signed_up" ? "already a Creatara user" : "already invited recently";
        toast.warning(`${s.email}: ${reason}`);
      });
      setRecipients([]);
      setNote("");
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invites");
    }
    setSending(false);
  };

  return (
    <div className="space-y-8" data-testid="referrals-page">
      <div>
        <h1 className="text-3xl font-bold mb-2">Invite Friends, Earn Credits</h1>
        <p className="text-muted-foreground">Get +{stats.credits_per_signup} content credits for every friend who signs up — and they get {stats.credits_per_signup} free credits too.</p>
      </div>

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass"><CardContent className="p-5 text-center"><Gift className="w-8 h-8 text-amber-500 mx-auto mb-2" /><p className="text-3xl font-bold">{stats.total_credits}</p><p className="text-xs text-muted-foreground mt-1">Credits Earned</p></CardContent></Card>
        <Card className="glass"><CardContent className="p-5 text-center"><Users className="w-8 h-8 text-teal-500 mx-auto mb-2" /><p className="text-3xl font-bold">{stats.total_referrals}</p><p className="text-xs text-muted-foreground mt-1">Successful Referrals</p></CardContent></Card>
        <Card className="glass"><CardContent className="p-5 text-center"><Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" /><p className="text-3xl font-bold">{stats.pending_invites?.length || 0}</p><p className="text-xs text-muted-foreground mt-1">Pending Invites</p></CardContent></Card>
        <Card className="glass"><CardContent className="p-5 text-center"><TrendingUp className="w-8 h-8 text-cyan-400 mx-auto mb-2" /><p className="text-3xl font-bold">{Math.round((stats.conversion_rate || 0) * 100)}%</p><p className="text-xs text-muted-foreground mt-1">Conversion Rate</p></CardContent></Card>
      </div>

      {/* Email invite — primary action */}
      <Card className="glass border-violet-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-violet-500" />Send a personal invite</CardTitle>
          <p className="text-sm text-muted-foreground">Highest conversion path — branded email arrives in their inbox with your name and your note.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Their email</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                placeholder="friend@email.com"
                className="bg-white/5 border-white/10"
                data-testid="invite-email-input"
              />
              <Button onClick={addRecipient} variant="outline" className="rounded-md whitespace-nowrap" data-testid="invite-add-btn">+ Add</Button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="invite-recipients">
                {recipients.map(r => (
                  <Badge key={r} className="bg-violet-500/15 text-violet-300 border border-violet-500/30 px-2 py-1">
                    {r}
                    <button onClick={() => removeRecipient(r)} className="ml-2 hover:text-white" aria-label={`Remove ${r}`}>×</button>
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground self-center">{recipients.length}/5</span>
              </div>
            )}
          </div>
          <div>
            <Label>Personal note (optional)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="Add a short note — increases open rates by ~40%."
              rows={3}
              className="mt-1 w-full p-3 rounded-md bg-white/5 border border-white/10 text-sm resize-none focus:outline-none focus:border-violet-500/50"
              data-testid="invite-note"
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{note.length}/500</p>
          </div>
          <Button onClick={sendInvites} disabled={sending || recipients.length === 0} className="w-full btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="invite-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Send {recipients.length || ""} invite{recipients.length !== 1 ? "s" : ""}</>}
          </Button>
        </CardContent>
      </Card>

      {/* Share via channel */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Or share your link</CardTitle>
          <p className="text-sm text-muted-foreground">Drop your link anywhere your audience hangs out.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 p-3 rounded-lg bg-white/5 border border-white/10 font-mono text-xs sm:text-sm truncate" data-testid="referral-link">{referralLink}</div>
            <Button onClick={copyLink} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="copy-link-btn"><Copy className="w-4 h-4 mr-2" />Copy</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Button onClick={shareTwitter} variant="outline" className="rounded-full" data-testid="share-twitter"><Twitter className="w-4 h-4 mr-2" />Twitter</Button>
            <Button onClick={shareLinkedIn} variant="outline" className="rounded-full" data-testid="share-linkedin"><Linkedin className="w-4 h-4 mr-2" />LinkedIn</Button>
            <Button onClick={shareWhatsApp} variant="outline" className="rounded-full" data-testid="share-whatsapp"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</Button>
            <Button onClick={shareEmail} variant="outline" className="rounded-full" data-testid="share-email"><Mail className="w-4 h-4 mr-2" />Email</Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending invites */}
      {stats.pending_invites && stats.pending_invites.length > 0 && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" />Pending invites</CardTitle>
            <p className="text-sm text-muted-foreground">You've sent {stats.invites_sent} invites, {stats.invites_converted} have signed up. {stats.pending_invites.length} still cooking.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.pending_invites.map((p) => (
                <div key={p.email} className="flex items-center justify-between p-3 rounded-lg bg-white/5 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center text-blue-400">{p.email.charAt(0).toUpperCase()}</div>
                    <span>{p.email}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.sent_at ? new Date(p.sent_at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Successful referrals */}
      {stats.referrals && stats.referrals.length > 0 && (
        <Card className="glass">
          <CardHeader><CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-teal-500" />Friends who signed up</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center"><span className="text-teal-500 font-semibold">{r.name?.charAt(0)}</span></div>
                    <div>
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.date ? new Date(r.date).toLocaleDateString() : ""}{r.plan !== "free" ? ` · ${r.plan}` : ""}</p>
                    </div>
                  </div>
                  <Badge className="bg-teal-500/20 text-teal-500">+{r.credits} credits</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Social proof — leaderboard */}
      {leaderboard.length > 0 && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" />Top referrers — all time</CardTitle>
            <p className="text-sm text-muted-foreground">Real Creatara users earning real credits by inviting their network.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.map((l, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? "bg-amber-500/20 text-amber-400" : i === 1 ? "bg-slate-400/20 text-slate-300" : i === 2 ? "bg-orange-700/30 text-orange-400" : "bg-white/5 text-muted-foreground"}`}>#{i + 1}</div>
                    <span className="font-medium">{l.name}</span>
                    {l.plan !== "free" && <Badge variant="secondary" className="text-[10px] capitalize">{l.plan}</Badge>}
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-amber-400">{l.referrals}</span>{" "}
                    <span className="text-muted-foreground">referral{l.referrals !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
